// TasksListPage — список задач с фильтрами + пагинацией + FAB "Создать".
// Использует TanStack Query (`useTasksQuery`) для загрузки данных.
import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Fab,
  Tooltip,
  TablePagination,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { TaskFilters, type TaskFiltersValue } from './components/TaskFilters';
import { TaskStatusChip } from './components/TaskStatusChip';
import { TaskPriorityChip } from './components/TaskPriorityChip';
import { tasksApi, type Task } from '@/api/tasks';
import { dealsApi } from '@/api/deals';
import { useUserMap, resolveUserName } from '@/api/users';
import { formatDate } from '@/lib/format';
import { useTableSort, type TableSortState } from '@/hooks/useTableSort';
import { t } from '@/i18n';

const DEFAULT_PAGE_SIZE = 20;

/** Поля сортировки списка задач (U-23: whitelist backend createdAt|updatedAt|dueAt). */
type TaskSortField = 'createdAt' | 'dueAt';

/** Состояние сортировки: default createdAt,desc (backend default created_at DESC). */
const DEFAULT_SORT: TableSortState<TaskSortField> = { field: 'createdAt', direction: 'desc' };

/** Страница списка задач: фильтры + таблица + пагинация + FAB. */
export function TasksListPage(): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<TaskFiltersValue>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  // U-23: состояние сортировки (канон DealsListPage: новый field → asc, активный → toggle).
  const { sort, handleSortClick } = useTableSort<TaskSortField>(DEFAULT_SORT, setPage);

  const query = useQuery({
    queryKey: ['tasks', page, filters, sort],
    queryFn: () =>
      tasksApi.list({
        page,
        size: DEFAULT_PAGE_SIZE,
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.priority ? { priority: filters.priority } : {}),
        ...(filters.assigneeUserId ? { assigneeUserId: filters.assigneeUserId } : {}),
        sort: `${sort.field},${sort.direction}`,
      }),
  });

  // R23 / баг D21: резолвим только исполнителей видимых строк (ids-режим /api/users?ids=).
  const assigneeIds = useMemo(
    () =>
      (query.data?.content ?? [])
        .map((task) => task.assigneeUserId)
        .filter((id): id is string => Boolean(id)),
    [query.data],
  );
  const userMap = useUserMap(assigneeIds);

  // R38b / U-9: колонка «Сделка» — резолв названия по id (dealsApi.listByIds-аналога нет,
  // backend GET /api/deals поддерживает только поштучный GET /api/deals/{id}).
  // FIX-C (S4): retry=false + признак недоступности сделки — по контракту backend
  // (DealService.getById) canceled-сделка для non-admin → NotFound (soft-delete),
  // поэтому UI показывает «Сделка удалена» (некликабельно), а не вечную «…».
  const dealIds = useMemo(
    () => [
      ...new Set(
        (query.data?.content ?? [])
          .map((task) => task.dealId)
          .filter((id): id is string => Boolean(id)),
      ),
    ],
    [query.data],
  );
  const dealQueries = useQueries({
    queries: dealIds.map((dealId) => ({
      queryKey: ['deal', dealId],
      queryFn: () => dealsApi.get(dealId),
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });
  const dealMap = useMemo(
    () =>
      new Map(
        dealQueries.flatMap((q, index) => {
          const dealId = dealIds[index];
          return q.data ? [[dealId, q.data.title] as const] : [];
        }),
      ),
    [dealQueries, dealIds],
  );
  /** Множество id сделок, недоступных на backend (404/cancelled) — показываем «Сделка удалена». */
  const dealFailedIds = useMemo(
    () =>
      new Set(
        dealQueries.flatMap((q, index) => (q.isError && dealIds[index] ? [dealIds[index]] : [])),
      ),
    [dealQueries, dealIds],
  );

  return (
    <Box>
      <PageHeader
        title="Задачи"
        subtitle="Управление задачами вашего отдела"
        actions={
          <Tooltip title="Фильтры">
            <IconButton onClick={() => setDrawerOpen(true)} aria-label="Открыть фильтры">
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        }
      />
      {query.isLoading ? (
        <LoadingSpinner label="Загрузка задач…" />
      ) : query.isError ? (
        <EmptyState
          title="Не удалось загрузить задачи"
          description={query.error instanceof Error ? query.error.message : 'Неизвестная ошибка'}
        />
      ) : !query.data || query.data.content.length === 0 ? (
        <EmptyState
          title="Нет задач"
          description="Создайте первую задачу, нажав кнопку ниже"
          actionLabel="Создать задачу"
          onAction={() => navigate('/tasks/new')}
        />
      ) : (
        <Paper>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Название</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Приоритет</TableCell>
                  <TableCell>Исполнитель</TableCell>
                  {/* U-23: сортируемые даты — «Создана» (createdAt) + «Срок» (dueAt). */}
                  <TableCell sortDirection={sort.field === 'createdAt' ? sort.direction : false}>
                    <TableSortLabel
                      active={sort.field === 'createdAt'}
                      direction={sort.field === 'createdAt' ? sort.direction : 'desc'}
                      onClick={() => handleSortClick('createdAt')}
                    >
                      {t('tasks.list.createdAt')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sort.field === 'dueAt' ? sort.direction : false}>
                    <TableSortLabel
                      active={sort.field === 'dueAt'}
                      direction={sort.field === 'dueAt' ? sort.direction : 'asc'}
                      onClick={() => handleSortClick('dueAt')}
                    >
                      {t('tasks.list.dueAt')}
                    </TableSortLabel>
                  </TableCell>
                  {/* R38b / U-9: связанная сделка (название, не сырой UUID — U-15). */}
                  <TableCell>{t('tasks.deal')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.content.map((task: Task) => {
                  const dealTitle = task.dealId ? dealMap.get(task.dealId) : undefined;
                  const dealUnavailable = task.dealId
                    ? dealFailedIds.has(task.dealId)
                    : false;
                  return (
                    <TableRow
                      key={task.id}
                      hover
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {task.taskNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={task.description ?? 'Нет описания'}
                          placement="top"
                          enterDelay={300}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {task.title}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <TaskStatusChip status={task.status} />
                      </TableCell>
                      <TableCell>
                        <TaskPriorityChip priority={task.priority} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {resolveUserName(userMap, task.assigneeUserId)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(task.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(task.dueAt)}
                        </Typography>
                      </TableCell>
                      {/* FIX-C (S4): удалённая сделка → некликабельная подпись,
                          без ссылки на 404 (контракт DealService.getById). */}
                      <TableCell>
                        {dealTitle ? (
                          <Link
                            component={RouterLink}
                            to={`/crm/deals/${task.dealId}`}
                            variant="body2"
                            underline="hover"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {dealTitle}
                          </Link>
                        ) : dealUnavailable ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('tasks.dealDeleted')}
                          </Typography>
                        ) : task.dealId ? (
                          <Typography variant="body2" color="text.secondary">
                            …
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={query.data.total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={DEFAULT_PAGE_SIZE}
            rowsPerPageOptions={[DEFAULT_PAGE_SIZE]}
            labelRowsPerPage={t('common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of')} ${count}`}
          />
        </Paper>
      )}
      <Stack direction="row" justifyContent="flex-end">
        <Fab
          component={RouterLink}
          to="/tasks/new"
          color="primary"
          aria-label="Создать задачу"
          sx={{ position: 'fixed', bottom: 24, right: 24 }}
        >
          <AddIcon />
        </Fab>
      </Stack>
      <TaskFilters
        value={filters}
        onChange={setFilters}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </Box>
  );
}
